// System Design case studies for Learn. Each follows the interview flow taught
// in "How to Approach a System Design Interview": requirements → estimates →
// entities and API → high-level design → deep dives → level expectations.

export const systemDesignCaseStudies = [
  {
    slug: 'design-url-shortener',
    title: 'Case Study: Design a URL Shortener',
    summary: 'Short codes, a read-heavy redirect path, caching and analytics, walked through like a real interview.',
    readTime: 13,
    content: [
      { type: 'h2', text: 'The prompt' },
      { type: 'p', text: '“Design a service like bit.ly that turns long URLs into short links and redirects people who open them.” It looks small, which is exactly why it is popular: you can finish a full design in 45 minutes, and every step of the method gets exercised.' },

      { type: 'h2', text: '1. Requirements' },
      { type: 'code', language: 'text', code: `Functional
  1. Create a short link for a long URL (optional custom alias, optional expiry)
  2. Opening the short link redirects to the long URL
  3. Link owners can see click counts

Non-functional
  - Redirects must be fast: p99 under ~50 ms
  - Highly available: a broken redirect breaks someone's campaign
  - Codes should be hard to guess for private links
  - Click counts can be a few minutes behind

Out of scope: user accounts UI, link editing, billing` },

      { type: 'h2', text: '2. Estimates' },
      { type: 'code', language: 'text', code: `Creates:    ~100M per month        ≈ 40 per second
Redirects:  ~10B per month         ≈ 4,000 per second, peaks of ~40,000
Ratio:      ~100 reads per write   → optimise the redirect path above everything
Storage:    ~500 bytes × 100M × 12 × 5 years ≈ 3 TB  → fits comfortably in a sharded store` },
      { type: 'p', text: 'The read-to-write ratio is the one number that shapes this design. Everything about redirects should be cached; creation can be comparatively simple.' },

      { type: 'h2', text: '3. Core entities and API' },
      { type: 'code', language: 'http', code: `Link   { code, longUrl, ownerId, createdAt, expiresAt }
Click  { code, at, referrer, country }      (analytics, stored separately)

POST /links          { longUrl, alias?, expiresAt? }   → 201 { code, shortUrl }
GET  /{code}                                           → 302 Location: longUrl
GET  /links/{code}/stats                               → { clicks, byDay, ... }` },

      { type: 'h2', text: '4. High-level design' },
      {"type": "diagram", "title": "URL shortener: high-level design", "caption": "Creation is a low-volume path; redirects are the hot path and are served mostly from cache. Click events go through a queue so analytics never slows a redirect (dashed = asynchronous).", "cols": 5, "rows": 4, "nodes": [{"id": "client", "label": "Browser / app", "kind": "client", "col": 0, "row": 1}, {"id": "lb", "label": "Load balancer", "kind": "edge", "col": 1, "row": 1}, {"id": "link", "label": "Link service", "kind": "service", "col": 2, "row": 0, "sub": "create links"}, {"id": "alloc", "label": "ID allocator", "kind": "service", "col": 3, "row": 0, "sub": "hands out ID ranges"}, {"id": "redirect", "label": "Redirect service", "kind": "service", "col": 2, "row": 2, "sub": "302 to long URL"}, {"id": "cache", "label": "Redis", "kind": "cache", "col": 3, "row": 2, "sub": "hot links"}, {"id": "db", "label": "Links DB", "kind": "db", "col": 4, "row": 1, "sub": "key: code"}, {"id": "q", "label": "Click events", "kind": "queue", "col": 2, "row": 3}, {"id": "an", "label": "Analytics workers", "kind": "worker", "col": 3, "row": 3}, {"id": "stats", "label": "Stats store", "kind": "db", "col": 4, "row": 3}], "edges": [{"from": "client", "to": "lb"}, {"from": "lb", "to": "link", "label": "create"}, {"from": "lb", "to": "redirect", "label": "redirect"}, {"from": "link", "to": "alloc", "label": "next ID"}, {"from": "link", "to": "db", "label": "save"}, {"from": "redirect", "to": "cache", "label": "1. lookup"}, {"from": "redirect", "to": "db", "label": "2. on miss"}, {"from": "redirect", "to": "q", "label": "click", "dashed": true}, {"from": "q", "to": "an", "dashed": true}, {"from": "an", "to": "stats", "label": "counts"}]},
      { type: 'p', text: 'Two paths: a low-volume create path and a very high-volume redirect path. Separating them lets each scale independently, and keeps analytics off the critical path.' },

      { type: 'h2', text: '5. Deep dive: generating short codes' },
      { type: 'p', text: 'With a 62-character alphabet (a–z, A–Z, 0–9), seven characters give about 3.5 trillion combinations, far more than we need.' },
      { type: 'table', headers: ['Option', 'How', 'Verdict'], rows: [
        ['Hash the long URL', 'Take the first 7 characters of a hash', 'Collisions need handling; same URL always same code (sometimes undesirable)'],
        ['Random code', 'Generate 7 random characters, retry on collision', 'Simple and unguessable; needs a uniqueness check'],
        ['Global counter', 'Increment a counter, encode in base62', 'No collisions, but a single bottleneck and guessable'],
        ['Counter ranges', 'Each server reserves a block (e.g. 1M IDs) and encodes locally', 'No per-request coordination, no collisions; a strong answer']
      ] },
      { type: 'code', variants: [
        { language: 'java', code: `public class Base62 {
    static final String ALPHABET = "0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ";

    static String encode(long n) {
        StringBuilder code = new StringBuilder();
        do {
            code.insert(0, ALPHABET.charAt((int) (n % 62)));
            n /= 62;
        } while (n > 0);
        while (code.length() < 7) code.insert(0, '0');
        return code.toString();
    }

    public static void main(String[] args) {
        System.out.println(encode(1) + " " + encode(61) + " " + encode(62) + " " + encode(1_000_000));
    }
}`, output: `0000001 000000Z 0000010 0004c92` },
        { language: 'python', code: `ALPHABET = "0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ"

def encode(n):
    code = ""
    while True:
        code = ALPHABET[n % 62] + code
        n //= 62
        if n == 0:
            break
    return code.rjust(7, "0")

print(encode(1), encode(61), encode(62), encode(1_000_000))`, output: `0000001 000000Z 0000010 0004c92` },
        { language: 'javascript', code: `const ALPHABET = "0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ";

function encode(n) {
  let code = "";
  do {
    code = ALPHABET[n % 62] + code;
    n = Math.floor(n / 62);
  } while (n > 0);
  return code.padStart(7, "0");
}

console.log(encode(1), encode(61), encode(62), encode(1_000_000));`, output: `0000001 000000Z 0000010 0004c92` }
      ] },
      { type: 'tip', text: 'If guessable codes matter, scramble the counter with a reversible permutation (for example multiplying by a large number modulo 62⁷) before encoding. You keep uniqueness without exposing the sequence.' },

      { type: 'h2', text: '6. Deep dive: making redirects fast' },
      { type: 'ul', items: [
        '**Cache-aside in Redis**: popular links get most of the clicks, so a cache with an LRU policy answers the vast majority of redirects from memory.',
        '**Cache negative results briefly**: bots requesting random codes should not hit the database each time.',
        '**Primary-key lookups only**: the database is partitioned by `code`, so every miss is a single-key read.',
        '**Edge caching**: for extremely popular links, CDN or edge functions can serve the redirect near the user.'
      ] },
      { type: 'h3', text: '301 or 302?' },
      { type: 'p', text: 'A 301 (permanent) redirect lets browsers cache the destination, reducing load, but repeat clicks never reach you, so analytics undercount. A 302 (temporary) sends every click through you. Because click counts are a requirement here, 302 is the better fit; mention the trade-off either way.' },

      { type: 'h2', text: '7. Deep dive: analytics without slowing redirects' },
      { type: 'p', text: 'Writing to a counter row on every redirect would turn the hottest read path into a write bottleneck, with popular links becoming hot rows. Instead, the redirect service publishes a small event to a queue and returns immediately. Workers aggregate events into per-minute counts and write those to a stats store. Counts arrive a little late, which the requirements allow.' },

      { type: 'h2', text: 'What each level should cover' },
      { type: 'table', headers: ['Level', 'Expected'], rows: [
        ['Junior', 'Clear API, a working design with a database and cache, a reasonable way to generate codes.'],
        ['Mid', 'Compares code-generation options, explains the read-heavy path, caching and the 301/302 choice.'],
        ['Senior', 'Counter ranges or scrambled IDs, analytics through a queue, hot links, failure of the cache or allocator, abuse prevention.']
      ] },

      { type: 'h2', text: 'Common mistakes' },
      { type: 'ul', items: [
        'Using one auto-increment counter without discussing it as a bottleneck.',
        'Updating click counters synchronously in the redirect.',
        'Ignoring custom alias collisions and reserved words (“api”, “admin”).',
        'Forgetting expiry and abuse (phishing links, scanning).'
      ] },

      { type: 'h2', text: 'Interview questions' },
      { type: 'qa', question: 'How do you support custom aliases?', answer: ['Store them in the same table and namespace as generated codes, rely on the primary key to reject duplicates, and keep a list of reserved words. Return 409 Conflict if the alias is taken.'] },
      { type: 'qa', question: 'What happens if Redis goes down?', answer: ['Redirects fall back to the database, which must be able to absorb the load for a while (or you degrade by rate-limiting). Run the cache with replicas so a single node failure does not empty it.'] },
      { type: 'qa', question: 'How do you stop people scanning for private links?', answer: ['Use non-sequential codes, rate-limit lookups of unknown codes per IP, and offer longer codes or authentication for sensitive links.'] },

      { type: 'exercise', title: 'Extend the design', task: 'Product wants “link in bio” pages: one URL per user that lists many links and shows per-link click counts in real time to the owner. What changes in the design?', solution: `- New entity: BioPage { userId, slug, links[] } (small, read-heavy → cache it)
- The bio page is a normal cached page; each link on it is a short link,
  so redirects and analytics are unchanged
- "Real-time" counts for the owner: keep per-link counters in Redis
  (INCR from the analytics workers) and push updates over SSE to the
  owner's dashboard; the stats store remains the durable source`, language: 'text' },

      { type: 'summary', items: [
        'The 100:1 read ratio drives the design: optimise and cache redirects.',
        'Counter ranges with base62 encoding give short, unique codes without per-request coordination.',
        'Keep analytics off the redirect path with a queue.',
        'Discuss 301 vs 302, aliases, expiry and abuse.'
      ] }
    ]
  },
  {
    slug: 'design-rate-limiter',
    title: 'Case Study: Design a Rate Limiter',
    summary: 'Algorithms, where to enforce limits, keeping counts correct across many servers, and failing safely.',
    readTime: 12,
    content: [
      { type: 'h2', text: 'The prompt' },
      { type: 'p', text: '“Design a rate limiter for our public API.” This question tests algorithms, distributed correctness and operational judgement at the same time.' },

      { type: 'h2', text: '1. Requirements' },
      { type: 'code', language: 'text', code: `Functional
  1. Limit requests per client (API key or user; IP for anonymous traffic)
  2. Different limits per endpoint and per plan (free vs paid)
  3. Rejected requests get 429 Too Many Requests with a Retry-After hint

Non-functional
  - Adds very little latency (a few ms) to every request
  - Accurate across many API servers
  - The limiter failing must not take the whole API down` },

      { type: 'h2', text: '2. Estimates' },
      { type: 'code', language: 'text', code: `Traffic:  ~50,000 requests/second across the API
Clients:  ~1M active API keys
State:    a few counters per key ≈ tens of bytes × 1M ≈ well under 1 GB
          → all limiter state fits in memory (Redis)` },

      { type: 'h2', text: '3. Where should the limiter run?' },
      { type: 'table', headers: ['Location', 'Pros', 'Cons'], rows: [
        ['In each API server, in memory', 'No network hop', 'Each server counts separately: limits are wrong with many servers'],
        ['API gateway / edge', 'Protects everything behind it; one place to configure', 'Coarse; may not know business context (plan, endpoint cost)'],
        ['Shared store (Redis) called by servers or gateway', 'Accurate across servers', 'One network hop per request; must handle store failure']
      ] },
      {"type": "diagram", "title": "Rate limiter: layered design", "caption": "Coarse per-IP limits at the edge absorb abuse; the gateway enforces precise per-key limits using atomic token-bucket updates in Redis, with limit rules cached from configuration.", "cols": 4, "rows": 3, "nodes": [{"id": "client", "label": "API client", "kind": "client", "col": 0, "row": 1}, {"id": "cdn", "label": "Edge / CDN", "kind": "edge", "col": 1, "row": 1, "sub": "coarse per-IP limits"}, {"id": "gw", "label": "API gateway", "kind": "edge", "col": 2, "row": 1, "sub": "per-key limits"}, {"id": "redis", "label": "Redis", "kind": "cache", "col": 2, "row": 0, "sub": "token buckets"}, {"id": "rules", "label": "Limit rules", "kind": "db", "col": 2, "row": 2, "sub": "per plan & endpoint"}, {"id": "api", "label": "API services", "kind": "service", "col": 3, "row": 1}], "edges": [{"from": "client", "to": "cdn"}, {"from": "cdn", "to": "gw"}, {"from": "gw", "to": "redis", "label": "atomic check"}, {"from": "gw", "to": "rules", "label": "cached config", "dashed": true}, {"from": "gw", "to": "api", "label": "allowed"}]},
      { type: 'p', text: 'A common, strong answer combines layers: coarse per-IP limits at the edge to absorb abuse, and accurate per-key limits in the gateway backed by Redis.' },

      { type: 'h2', text: '4. Choosing an algorithm' },
      { type: 'table', headers: ['Algorithm', 'Idea', 'Trade-off'], rows: [
        ['Fixed window', 'Count requests per calendar minute', 'Simple, but allows bursts of 2× at window edges'],
        ['Sliding window log', 'Store a timestamp per request', 'Exact, but memory grows with traffic'],
        ['Sliding window counter', 'Blend the current and previous window counts', 'Cheap and close to exact'],
        ['Token bucket', 'Tokens refill at a steady rate; each request spends one', 'Allows controlled bursts; the most common choice for APIs']
      ] },
      { type: 'code', variants: [
        { language: 'java', code: `import java.util.ArrayList;
import java.util.List;

public class TokenBucket {
    private final double capacity, refillPerSecond;
    private double tokens;
    private long lastMs = 0;

    TokenBucket(double capacity, double refillPerSecond) {
        this.capacity = capacity;
        this.refillPerSecond = refillPerSecond;
        this.tokens = capacity;                          // start full
    }

    boolean allow(long nowMs) {
        tokens = Math.min(capacity, tokens + (nowMs - lastMs) / 1000.0 * refillPerSecond);
        lastMs = nowMs;
        if (tokens >= 1) { tokens -= 1; return true; }
        return false;
    }

    public static void main(String[] args) {
        TokenBucket bucket = new TokenBucket(3, 1);     // burst of 3, then 1 per second
        List<String> results = new ArrayList<>();
        for (long t : new long[] {0, 0, 0, 0, 500, 1000, 1000, 3000}) {
            results.add(bucket.allow(t) ? "✓" : "✗");
        }
        System.out.println(String.join(" ", results));
    }
}`, output: `✓ ✓ ✓ ✗ ✗ ✓ ✗ ✓` },
        { language: 'python', code: `class TokenBucket:
    def __init__(self, capacity, refill_per_second):
        self.capacity = capacity
        self.refill = refill_per_second
        self.tokens = capacity              # start full
        self.last_ms = 0

    def allow(self, now_ms):
        self.tokens = min(self.capacity, self.tokens + (now_ms - self.last_ms) / 1000 * self.refill)
        self.last_ms = now_ms
        if self.tokens >= 1:
            self.tokens -= 1
            return True
        return False

bucket = TokenBucket(3, 1)                  # burst of 3, then 1 per second
times = [0, 0, 0, 0, 500, 1000, 1000, 3000]
print(" ".join("✓" if bucket.allow(t) else "✗" for t in times))`, output: `✓ ✓ ✓ ✗ ✗ ✓ ✗ ✓` },
        { language: 'javascript', code: `function createTokenBucket(capacity, refillPerSecond) {
  let tokens = capacity;
  let last = 0;                                   // time in ms
  return function allow(nowMs) {
    tokens = Math.min(capacity, tokens + ((nowMs - last) / 1000) * refillPerSecond);
    last = nowMs;
    if (tokens >= 1) { tokens -= 1; return true; }
    return false;
  };
}

const allow = createTokenBucket(3, 1);            // burst of 3, then 1 per second
const at = [0, 0, 0, 0, 500, 1000, 1000, 3000];
console.log(at.map((t) => (allow(t) ? "✓" : "✗")).join(" "));`, output: `✓ ✓ ✓ ✗ ✗ ✓ ✗ ✓` }
      ] },
      { type: 'p', text: 'The bucket starts full, so three requests at time 0 pass and the fourth is rejected. Half a second later only half a token has refilled. At one second a full token is available again, and by three seconds the bucket has refilled enough for more requests.' },

      { type: 'h2', text: '5. Deep dive: correctness across servers' },
      { type: 'p', text: 'Many API servers check the same client’s limit at the same moment. If each reads the count and then writes it back, two servers can both see 99 and both allow request number 100. The update must be atomic.' },
      { type: 'ul', items: [
        'For fixed windows, Redis `INCR` is atomic: increment and compare the returned value.',
        'For token buckets, run the refill-and-spend logic as a single Redis script so it executes atomically.',
        'Store keys with an expiry so idle clients do not use memory forever.'
      ] },

      { type: 'h2', text: '6. Deep dive: failure and performance' },
      { type: 'ul', items: [
        '**Redis unavailable**: decide per endpoint. Fail open (allow) for general traffic so an outage in the limiter does not become an API outage; fail closed for sensitive endpoints like login and OTP.',
        '**Latency**: keep Redis close to the API servers, use connection pooling, and avoid more than one round trip per request.',
        '**Hot clients**: a single heavy client lands on one Redis key; shard Redis by client key so load spreads across nodes.',
        '**Headers**: return remaining quota and reset time so well-behaved clients can slow down before hitting the limit.'
      ] },

      { type: 'h2', text: 'What each level should cover' },
      { type: 'table', headers: ['Level', 'Expected'], rows: [
        ['Junior', 'A fixed-window or token-bucket limiter, the 429 response, and why limits exist.'],
        ['Mid', 'Algorithm trade-offs, a shared store for multiple servers, atomic updates.'],
        ['Senior', 'Layered limiting, fail-open vs fail-closed per endpoint, hot keys, latency budget, client-facing headers and plan-based configuration.']
      ] },

      { type: 'h2', text: 'Common mistakes' },
      { type: 'ul', items: [
        'In-memory counters on each server with many servers behind a load balancer.',
        'Read-modify-write updates that race.',
        'Keying by IP only, which punishes many users behind one office or mobile network.',
        'Letting a limiter outage take down the API.'
      ] },

      { type: 'h2', text: 'Interview questions' },
      { type: 'qa', question: 'Why does a fixed window allow double the limit?', answer: ['A client can send the full limit at the very end of one window and again at the start of the next, so twice the limit passes within a short span. Sliding windows and token buckets smooth this out.'] },
      { type: 'qa', question: 'How would you rate limit across several regions?', answer: ['Usually per region, with each region getting a share of the global quota, because a single global counter would add cross-region latency to every request. For strict global limits, accept the latency or periodically reconcile counts.'] },
      { type: 'qa', question: 'Where should identity for limiting come from?', answer: ['From the authenticated API key or user, not from anything the client can change freely. For anonymous traffic use the client IP, taking care to read it correctly behind proxies.'] },

      { type: 'exercise', title: 'Design limits for a login endpoint', task: 'Attackers try passwords against many accounts. Design rate limits for POST /login that slow attackers down without locking out real users.', solution: `- Per IP: e.g. 20 attempts per 10 minutes (stops one machine hammering)
- Per account: e.g. 5 failed attempts per 15 minutes, then require a
  CAPTCHA or a short cooldown (stops distributed guessing on one account)
- Count failures, not successes; reset the account counter on success
- Fail closed if the limiter store is down (security-sensitive endpoint)
- Alert on spikes across many accounts (credential stuffing)`, language: 'text' },

      { type: 'summary', items: [
        'Token bucket is the usual choice: steady rate with controlled bursts.',
        'Use a shared store and atomic operations for accuracy across servers.',
        'Layer limits: coarse at the edge, precise per client in the gateway.',
        'Decide fail-open or fail-closed per endpoint.'
      ] }
    ]
  },
  {
    slug: 'design-chat-app',
    title: 'Case Study: Design a Chat App',
    summary: 'Persistent connections, message delivery across servers, ordering, offline users and group chats.',
    readTime: 14,
    content: [
      { type: 'h2', text: 'The prompt' },
      { type: 'p', text: '“Design a messaging app like WhatsApp.” This brings together real-time connections, durable storage, ordering and delivery guarantees, and is a favourite for mid and senior interviews.' },

      { type: 'h2', text: '1. Requirements' },
      { type: 'code', language: 'text', code: `Functional
  1. One-to-one and group chats (groups up to a few hundred members)
  2. Messages are delivered in real time when the recipient is online
  3. Offline users receive messages when they come back (and a push notification)
  4. Sent / delivered / read status

Non-functional
  - Low latency: well under a second when both users are online
  - Messages are never lost once the sender sees "sent"
  - Messages appear in the same order for everyone in a conversation

Out of scope: voice and video calls, end-to-end encryption details, stories` },

      { type: 'h2', text: '2. Estimates' },
      { type: 'code', language: 'text', code: `Daily users:    ~100M, each sending ~40 messages/day
Messages:       ~4B/day ≈ 46,000/s average, peaks several times higher
Connections:    tens of millions open at once → a dedicated connection tier
Storage:        ~200 bytes × 4B/day ≈ 800 GB/day → a horizontally scalable store` },

      { type: 'h2', text: '3. Entities and API' },
      { type: 'code', language: 'text', code: `Conversation { id, type (direct|group), members[] }
Message      { conversationId, seq, senderId, body, clientMsgId, sentAt }
Receipt      { conversationId, userId, deliveredUpTo, readUpTo }

Over the persistent connection (WebSocket):
  → send     { conversationId, clientMsgId, body }
  ← ack      { clientMsgId, seq }              (stored: show "sent")
  ← message  { conversationId, seq, senderId, body }
  → receipt  { conversationId, readUpTo }

Over HTTP:
  GET /conversations/{id}/messages?after={seq}   (catch up after reconnecting)` },

      { type: 'h2', text: '4. High-level design' },
      {"type": "diagram", "title": "Chat app: high-level design", "caption": "Gateways only hold connections. The chat service stores each message first, then looks up the recipient’s gateway and publishes the message to it; offline recipients get a push notification.", "cols": 4, "rows": 4, "nodes": [{"id": "a", "label": "Phone A", "kind": "client", "col": 0, "row": 0}, {"id": "b", "label": "Phone B", "kind": "client", "col": 0, "row": 2}, {"id": "g1", "label": "Gateway 1", "kind": "edge", "col": 1, "row": 0, "sub": "WebSockets"}, {"id": "g2", "label": "Gateway 2", "kind": "edge", "col": 1, "row": 2, "sub": "WebSockets"}, {"id": "chat", "label": "Chat service", "kind": "service", "col": 2, "row": 1, "sub": "assigns seq, stores"}, {"id": "store", "label": "Message store", "kind": "db", "col": 3, "row": 0, "sub": "by conversationId"}, {"id": "reg", "label": "Presence & routing", "kind": "cache", "col": 3, "row": 1, "sub": "user → gateway"}, {"id": "ps", "label": "Pub/sub", "kind": "queue", "col": 3, "row": 2}, {"id": "push", "label": "Push service", "kind": "external", "col": 3, "row": 3, "sub": "APNs / FCM"}], "edges": [{"from": "a", "to": "g1", "label": "ws", "both": true}, {"from": "b", "to": "g2", "label": "ws", "both": true}, {"from": "g1", "to": "chat", "label": "send"}, {"from": "chat", "to": "store", "label": "1. store"}, {"from": "chat", "to": "reg", "label": "2. lookup"}, {"from": "chat", "to": "ps", "label": "3. publish"}, {"from": "ps", "to": "g2", "label": "deliver"}, {"from": "chat", "to": "push", "label": "if offline", "dashed": true, "labelAt": 0.3}]},
      { type: 'p', text: 'Gateways only manage connections, so they stay simple and can hold huge numbers of them. The chat service is stateless and does the real work: assigning order, storing messages and routing them.' },

      { type: 'h2', text: '5. Deep dive: sending and delivering a message' },
      { type: 'ol', items: [
        'The sender’s app gives each message a `clientMsgId` and sends it over the WebSocket.',
        'The chat service assigns the next sequence number for that conversation and writes the message durably.',
        'Only after the write succeeds does it acknowledge the sender, which shows “sent”.',
        'It looks up which gateway each recipient is connected to and delivers the message there.',
        'The recipient’s app acknowledges receipt, which becomes “delivered”; opening the chat sends “read”.'
      ] },
      { type: 'callout', variant: 'tip', title: 'Retries without duplicates', text: 'If the sender loses connection before the ack, it resends with the same `clientMsgId`. The server stores `(conversationId, clientMsgId)` as unique, so the retry returns the original sequence number instead of creating a duplicate.' },

      { type: 'h2', text: '6. Deep dive: ordering' },
      { type: 'p', text: 'Ordering across all of WhatsApp is neither possible nor necessary; ordering within a conversation is what users see. Give each conversation a monotonically increasing sequence number, assigned in one place for that conversation (for example by routing all writes for a conversation to the same partition). Clients sort by `seq`, and gaps tell them a message is missing.' },

      { type: 'h2', text: '7. Deep dive: offline users and reconnecting' },
      { type: 'ul', items: [
        'Messages are stored before delivery is attempted, so nothing depends on the recipient being online.',
        'If the recipient has no live connection, send a push notification.',
        'On reconnect, the app asks for messages after the last `seq` it has for each conversation.',
        'Receipts (`deliveredUpTo`, `readUpTo`) are just numbers per user per conversation, so they are cheap to store and sync.'
      ] },

      { type: 'h2', text: '8. Deep dive: group chats' },
      { type: 'p', text: 'For a group, the message is stored once and then delivered to each online member’s gateway. With a few hundred members this fan-out is fine. For very large broadcast channels, store once and let members pull recent messages when they open the channel, instead of pushing to everyone.' },

      { type: 'h2', text: 'Choosing the message store' },
      { type: 'p', text: 'The dominant query is “the latest N messages in a conversation, and everything after sequence X”, with very high write volume. A wide-column store partitioned by `conversationId` and sorted by `seq` serves both as sequential reads and scales writes horizontally. A sharded relational database also works if you partition the same way.' },

      { type: 'h2', text: 'What each level should cover' },
      { type: 'table', headers: ['Level', 'Expected'], rows: [
        ['Junior', 'WebSockets for real time, a message table, storing before delivering.'],
        ['Mid', 'Gateway tier and routing between gateways, offline delivery with catch-up, receipts.'],
        ['Senior', 'Per-conversation ordering, idempotent retries, storage partitioning, large groups, reconnect storms, multi-device.']
      ] },

      { type: 'h2', text: 'Common mistakes' },
      { type: 'ul', items: [
        'Acknowledging the sender before the message is stored.',
        'Relying on timestamps from phones to order messages.',
        'Using HTTP polling as the main delivery mechanism at this scale.',
        'Forgetting the catch-up path after reconnecting.'
      ] },

      { type: 'h2', text: 'Interview questions' },
      { type: 'qa', question: 'How do you support one user on several devices?', answer: ['Each device has its own connection and its own read position. Deliver messages to all of a user’s connected devices, and let each device catch up from its own last sequence number. Read receipts can sync across devices through the same receipt records.'] },
      { type: 'qa', question: 'How would you show “online” and “typing…”?', answer: ['Presence comes from connection heartbeats stored with a short expiry. Typing indicators are short-lived events sent through the same connections and never stored. Both should be sent only to people currently viewing the conversation, to avoid flooding everyone’s contacts.'] },
      { type: 'qa', question: 'What happens when a gateway server restarts?', answer: ['Its clients disconnect and reconnect to other gateways (with random backoff to avoid a stampede), re-register in the routing table, and fetch any messages they missed using their last sequence numbers. No messages are lost because they were stored before delivery.'] },

      { type: 'exercise', title: 'Add message editing and deletion', task: 'Users want to edit or delete a message they sent within 15 minutes, and everyone in the conversation should see the change. How would you add this?', solution: `- Store edits/deletes as new events in the conversation stream:
  { type: "edit", targetSeq, newBody } or { type: "delete", targetSeq }
  each with its own seq, so ordering and catch-up keep working
- Check on the server: sender = original sender, within 15 minutes
- Clients apply the event to the target message when rendering
- Deleted content is removed from storage (privacy), leaving a
  "message deleted" placeholder`, language: 'text' },

      { type: 'summary', items: [
        'A connection tier of gateways plus a stateless chat service.',
        'Store first, acknowledge second, deliver third.',
        'Order per conversation with sequence numbers; dedupe retries with client IDs.',
        'Offline users get push notifications and catch up by sequence number.'
      ] }
    ]
  },
  {
    slug: 'design-news-feed',
    title: 'Case Study: Design a News Feed',
    summary: 'Fan-out on write vs on read, the celebrity problem, feed storage, ranking and pagination.',
    readTime: 13,
    content: [
      { type: 'h2', text: 'The prompt' },
      { type: 'p', text: '“Design the home feed for a social app where users follow each other.” The key challenge is serving each user a personalised feed quickly when their friends post constantly and some accounts have millions of followers.' },

      { type: 'h2', text: '1. Requirements' },
      { type: 'code', language: 'text', code: `Functional
  1. Users create posts (text, images)
  2. Users follow other users
  3. Users see a home feed of posts from accounts they follow, newest or ranked
  4. Infinite scroll through the feed

Non-functional
  - Feed loads fast: p99 under ~300 ms
  - A new post appears in followers' feeds within seconds (not instantly)
  - Very read-heavy: people scroll far more than they post` },

      { type: 'h2', text: '2. Estimates' },
      { type: 'code', language: 'text', code: `Daily users:   ~200M, ~10 feed loads each  ≈ 23,000 feed reads/s
Posts:         ~50M per day                 ≈ 600 posts/s
Follows:       average ~200, some accounts have tens of millions of followers` },
      { type: 'p', text: 'Reads outnumber posts roughly 40 to 1, and the follower distribution is extremely uneven. Both facts drive the main design decision below.' },

      { type: 'h2', text: '3. Entities and API' },
      { type: 'code', language: 'http', code: `Post    { id, authorId, body, mediaUrls, createdAt }
Follow  { followerId, followeeId, createdAt }
Feed    per user: an ordered list of post ids (precomputed or computed on demand)

POST /posts                       { body, mediaUrls }   → 201 { id }
POST /users/{id}/follow                                 → 204
GET  /feed?cursor={cursor}&limit=20                     → { posts[], nextCursor }` },

      { type: 'h2', text: '4. The central decision: fan-out on write or on read?' },
      { type: 'table', headers: ['', 'Fan-out on write (push)', 'Fan-out on read (pull)'], rows: [
        ['When a user posts', 'Add the post id to every follower’s precomputed feed', 'Just store the post'],
        ['When a user opens the feed', 'Read one ready-made list: very fast', 'Fetch recent posts from everyone they follow and merge: slow'],
        ['Account with 20M followers', '20M feed writes per post: too slow and costly', 'No extra cost'],
        ['Inactive users', 'Their feeds are updated for nothing', 'No wasted work']
      ] },
      { type: 'p', text: 'The strong answer is a **hybrid**: fan out on write for normal accounts, and for accounts with very large followings, skip the fan-out and merge their recent posts in at read time.' },

      { type: 'h2', text: '5. High-level design' },
      {"type": "diagram", "title": "News feed: hybrid fan-out", "caption": "Posting writes the post and fans its ID out asynchronously into followers’ cached feeds. Reading takes the precomputed IDs, merges in recent posts from very large accounts, ranks them, and hydrates full posts.", "cols": 4, "rows": 3, "nodes": [{"id": "client", "label": "App", "kind": "client", "col": 0, "row": 1}, {"id": "post", "label": "Post service", "kind": "service", "col": 1, "row": 0}, {"id": "posts", "label": "Posts DB", "kind": "db", "col": 2, "row": 0}, {"id": "fq", "label": "Fan-out queue", "kind": "queue", "col": 2, "row": 1}, {"id": "fw", "label": "Fan-out workers", "kind": "worker", "col": 3, "row": 1, "sub": "skip celebrity authors"}, {"id": "feedsvc", "label": "Feed service", "kind": "service", "col": 1, "row": 2, "sub": "merge, rank, hydrate"}, {"id": "feeds", "label": "Feed cache", "kind": "cache", "col": 3, "row": 2, "sub": "feed:{userId} → ids"}], "edges": [{"from": "client", "to": "post", "label": "create post"}, {"from": "client", "to": "feedsvc", "label": "load feed"}, {"from": "post", "to": "posts", "label": "save"}, {"from": "post", "to": "fq", "label": "post id", "dashed": true}, {"from": "fq", "to": "fw", "dashed": true}, {"from": "fw", "to": "feeds", "label": "push ids"}, {"from": "feedsvc", "to": "feeds", "label": "1. ids"}, {"from": "feedsvc", "to": "posts", "label": "2. hydrate"}]},

      { type: 'h2', text: '6. Deep dive: storing feeds' },
      { type: 'ul', items: [
        'Store only post ids in each user’s feed, not full posts, so updates and deletes stay cheap.',
        'Cap each feed (for example, the latest 500–1,000 ids); older history can be rebuilt on demand.',
        'Keep feeds in memory (Redis) for active users; rebuild a feed with a pull query when an inactive user returns.',
        'Hydrate ids into full posts with batched cache lookups, falling back to the posts database.'
      ] },

      { type: 'h2', text: '7. Deep dive: pagination' },
      { type: 'p', text: 'Offset pagination (`page=5`) breaks on a live feed: new posts shift everything, causing duplicates and gaps. Use a cursor: the client sends the position of the last item it saw, and the server returns items after it. For a chronological feed the cursor can be the last post’s `(createdAt, id)`; for ranked feeds, snapshot the ranked list briefly and page through the snapshot.' },

      { type: 'h2', text: '8. Deep dive: ranking' },
      { type: 'p', text: 'Many feeds are ranked rather than strictly chronological. A practical structure: gather a few hundred candidate posts (from the precomputed feed plus pulled celebrity posts), score them with signals like recency, the user’s past interactions with the author, and engagement, then return the top results. Keep ranking out of the write path so posting stays fast.' },

      { type: 'h2', text: 'What each level should cover' },
      { type: 'table', headers: ['Level', 'Expected'], rows: [
        ['Junior', 'Posts and follows tables, a working (pull-based) feed query, caching.'],
        ['Mid', 'Push vs pull with trade-offs, precomputed feeds, cursor pagination.'],
        ['Senior', 'The hybrid approach for celebrities, feed storage limits and rebuilds, hydration, ranking pipeline, deletes and privacy changes.']
      ] },

      { type: 'h2', text: 'Common mistakes' },
      { type: 'ul', items: [
        'Choosing only push or only pull without discussing very large accounts.',
        'Copying full post content into every follower’s feed.',
        'Offset pagination on a constantly changing list.',
        'Doing ranking work synchronously when a post is created.'
      ] },

      { type: 'h2', text: 'Interview questions' },
      { type: 'qa', question: 'How do you handle a deleted post that is already in millions of feeds?', answer: ['Do not try to remove it from every feed immediately. Mark the post as deleted, and filter it out at hydration time when feeds are read. Background cleanup can remove stale ids later.'] },
      { type: 'qa', question: 'Where do you draw the line for “celebrity”?', answer: ['A follower-count threshold (tuned by measuring fan-out cost) is a reasonable start. The real cost is followers multiplied by posting frequency, so a very active account with fewer followers might also be treated specially.'] },
      { type: 'qa', question: 'How does a new post appear instantly for its author?', answer: ['Insert it into the author’s own feed synchronously (or merge the author’s latest posts on the client). Other followers see it after asynchronous fan-out, within the seconds the requirements allow.'] },

      { type: 'exercise', title: 'Handle unfollows and privacy changes', task: 'A user unfollows someone, or an account switches from public to private. Followers’ precomputed feeds still contain their post ids. How do you make sure the feed stays correct?', solution: `- On read (hydration), check visibility: is the viewer still allowed to see
  this author? Filter out anything they can't
- Cache follow relationships and privacy settings so this check is cheap
- Optionally enqueue a cleanup job to remove that author's ids from the
  affected feed(s) so the filter has less to do over time
- Never rely only on the precomputed list for permission decisions`, language: 'text' },

      { type: 'summary', items: [
        'Feeds are read-heavy: precompute where you can.',
        'Hybrid fan-out: push for normal accounts, pull for very large ones.',
        'Store ids, hydrate on read, cap feed length.',
        'Use cursor pagination and keep ranking off the write path.'
      ] }
    ]
  }
];
